import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import AttendanceHistoryPanel from './AttendanceHistoryPanel';
import type { SchoolPayload } from './checkInTypes';

type CheckInSettingsTabProps = {
  school: SchoolPayload;
  onSchoolChange: (school: SchoolPayload) => void;
};

type PrintQrCount = 1 | 2 | 4 | 6;

const PRINT_OPTIONS: { count: PrintQrCount; label: string }[] = [
  { count: 1, label: 'Single QR code' },
  { count: 2, label: '2 QR codes' },
  { count: 4, label: '4 QR codes' },
  { count: 6, label: '6 QR codes' },
];

const PRINT_GRID_CLASS: Record<PrintQrCount, string> = {
  1: 'grid-cols-1 grid-rows-1',
  2: 'grid-cols-1 grid-rows-2',
  4: 'grid-cols-2 grid-rows-2',
  6: 'grid-cols-2 grid-rows-3',
};

const randomFourDigitCode = () =>
  String(Math.floor(1000 + Math.random() * 9000));

const AttendanceQrPrintSheet = ({
  url,
  schoolName,
  count,
}: {
  url: string;
  schoolName: string;
  count: PrintQrCount;
}) => {
  const sheet = (
    <div
      id="attendance-qr-print-root"
      className={`box-border hidden print:grid ${PRINT_GRID_CLASS[count]}`}
      aria-hidden
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="qr-print-cell">
          <div className="qr-print-code">
            <QRCodeSVG
              value={url}
              size={1200}
              level="M"
              includeMargin={false}
              style={{
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                aspectRatio: '1 / 1',
                objectFit: 'contain',
              }}
            />
          </div>
          <p className="qr-print-label">{schoolName}</p>
        </div>
      ))}
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(sheet, document.body);
};

const CheckInSettingsTab = ({ school, onSchoolChange }: CheckInSettingsTabProps) => {
  const [printCount, setPrintCount] = useState<PrintQrCount | null>(null);
  const [activeTab, setActiveTab] = useState<'qr-code' | 'attendance'>('attendance');
  const [codeEnabled, setCodeEnabled] = useState(school.attendanceSetting != null);
  const [attendanceCode, setAttendanceCode] = useState(
    school.attendanceSetting?.attendanceCode ?? '',
  );
  const [codeSaving, setCodeSaving] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const attendanceUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/attendance/${school.schoolId}`;
  }, [school.schoolId]);

  useEffect(() => {
    if (printCount == null) return;

    const previousTitle = document.title;
    document.title = `${school.name} — attendance QR`;
    document.body.classList.add('printing-qr');

    let cleared = false;
    const clearPrint = () => {
      if (cleared) return;
      cleared = true;
      document.body.classList.remove('printing-qr');
      document.title = previousTitle;
      setPrintCount(null);
    };

    const onAfterPrint = () => clearPrint();
    window.addEventListener('afterprint', onAfterPrint);

    const printTimer = window.setTimeout(() => {
      window.print();
    }, 150);

    const fallbackTimer = window.setTimeout(clearPrint, 60_000);

    return () => {
      window.clearTimeout(printTimer);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('afterprint', onAfterPrint);
      document.body.classList.remove('printing-qr');
      document.title = previousTitle;
    };
  }, [printCount, school.name]);

  const saveAttendanceSetting = async (
    nextSetting: { attendanceCode: string } | null,
  ) => {
    setCodeSaving(true);
    setCodeError(null);
    try {
      const res = await fetch('/api/school/checkin-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attendanceSetting: nextSetting }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        school?: SchoolPayload;
        error?: string;
      };
      if (!res.ok) {
        throw new Error(data.error || `Failed to save (${res.status})`);
      }
      if (!data.school) {
        throw new Error('Failed to save attendance code');
      }
      onSchoolChange(data.school);
      setCodeEnabled(data.school.attendanceSetting != null);
      setAttendanceCode(data.school.attendanceSetting?.attendanceCode ?? '');
    } catch (e) {
      setCodeError(e instanceof Error ? e.message : 'Failed to save');
      throw e;
    } finally {
      setCodeSaving(false);
    }
  };

  const handleToggleCode = async () => {
    if (codeSaving) return;
    if (codeEnabled) {
      try {
        await saveAttendanceSetting(null);
      } catch {
        // error already set
      }
      return;
    }
    setCodeEnabled(true);
    setCodeError(null);
  };

  const handleGenerateCode = async () => {
    if (codeSaving || !codeEnabled) return;
    const nextCode = randomFourDigitCode();
    setAttendanceCode(nextCode);
    try {
      await saveAttendanceSetting({ attendanceCode: nextCode });
    } catch {
      // error already set
    }
  };

  const handleSaveCode = async () => {
    if (codeSaving || !codeEnabled) return;
    const trimmed = attendanceCode.trim();
    if (!/^\d{4}$/.test(trimmed)) {
      setCodeError('Enter a 4-digit code.');
      return;
    }
    try {
      await saveAttendanceSetting({ attendanceCode: trimmed });
    } catch {
      // error already set
    }
  };

  return (
    <div className="space-y-6">
      <style>{`
        @media print {
          body.printing-qr {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body.printing-qr {
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            background: white !important;
          }
          body.printing-qr * {
            visibility: hidden !important;
          }
          body.printing-qr #attendance-qr-print-root,
          body.printing-qr #attendance-qr-print-root * {
            visibility: visible !important;
          }
          body.printing-qr #attendance-qr-print-root {
            display: grid !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: 100% !important;
            margin: 0 !important;
            padding: 0.15in !important;
            gap: 0.12in !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            background: white !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            break-after: avoid !important;
            break-inside: avoid !important;
          }
          body.printing-qr #attendance-qr-print-root .qr-print-cell {
            display: flex !important;
            min-width: 0 !important;
            min-height: 0 !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
            border: 1px dashed rgba(0, 0, 0, 0.2) !important;
            padding: 0.08in !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          body.printing-qr #attendance-qr-print-root .qr-print-code {
            display: flex !important;
            flex: 1 1 auto !important;
            align-items: center !important;
            justify-content: center !important;
            min-width: 0 !important;
            min-height: 0 !important;
            width: 100% !important;
            overflow: hidden !important;
          }
          body.printing-qr #attendance-qr-print-root .qr-print-label {
            flex: 0 0 auto !important;
            margin: 0.06in 0 0 !important;
            max-width: 100% !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            text-align: center !important;
            font-size: 9pt !important;
            font-weight: 600 !important;
            line-height: 1 !important;
            color: #000 !important;
          }
          @page {
            margin: 0.25in;
            size: letter portrait;
          }
        }
      `}</style>

      {printCount != null ? (
        <AttendanceQrPrintSheet
          url={attendanceUrl}
          schoolName={school.name}
          count={printCount}
        />
      ) : null}

      <div
        className="flex w-fit gap-1 rounded-full bg-ink/5 p-1"
        role="tablist"
        aria-label="Check-in settings"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'attendance'}
          onClick={() => setActiveTab('attendance')}
          className={`rounded-full px-4 py-2 font-sans text-sm font-semibold transition ${
            activeTab === 'attendance'
              ? 'bg-white text-ink shadow-sm ring-1 ring-ink/10'
              : 'text-ink/60 hover:text-ink'
          }`}
        >
          Attendance records
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'qr-code'}
          onClick={() => setActiveTab('qr-code')}
          className={`rounded-full px-4 py-2 font-sans text-sm font-semibold transition ${
            activeTab === 'qr-code'
              ? 'bg-white text-ink shadow-sm ring-1 ring-ink/10'
              : 'text-ink/60 hover:text-ink'
          }`}
        >
          QR code
        </button>
      </div>

      <div className={activeTab === 'qr-code' ? 'space-y-6' : 'hidden'}>
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Check-in / Check-out</h2>
          <p className="mt-1 font-sans text-sm text-ink/65">
            Share the attendance QR code for your school.
          </p>
        </div>

        <div className="rounded-2xl border-2 border-ink/10 bg-paper/40 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="font-display text-base font-semibold text-ink">Attendance code</h3>
              <p className="mt-1 font-sans text-sm text-ink/65">
                Require a 4-digit code when families check in or check out.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={codeEnabled}
              aria-label="Enable attendance code"
              disabled={codeSaving}
              onClick={() => void handleToggleCode()}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-2 font-sans text-sm font-semibold ring-2 transition disabled:opacity-50 ${
                codeEnabled
                  ? 'bg-skysoft/70 text-ink ring-ink/10'
                  : 'bg-white text-ink/70 ring-ink/10 hover:text-ink'
              }`}
            >
              <span
                aria-hidden
                className={`relative h-5 w-9 rounded-full transition ${
                  codeEnabled ? 'bg-ink' : 'bg-ink/20'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${
                    codeEnabled ? 'left-4' : 'left-0.5'
                  }`}
                />
              </span>
              {codeEnabled ? 'On' : 'Off'}
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block min-w-0 flex-1">
              <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                Code
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                disabled={!codeEnabled || codeSaving}
                value={attendanceCode}
                onChange={(event) => {
                  const next = event.target.value.replace(/\D/g, '').slice(0, 4);
                  setAttendanceCode(next);
                  setCodeError(null);
                }}
                placeholder="1234"
                className="mt-1 block w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-base font-semibold tracking-[0.2em] text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60 disabled:cursor-not-allowed disabled:bg-ink/5 disabled:text-ink/40"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!codeEnabled || codeSaving}
                onClick={() => void handleGenerateCode()}
                className="rounded-full bg-skysoft/70 px-4 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft disabled:cursor-not-allowed disabled:opacity-50"
              >
                Generate
              </button>
              <button
                type="button"
                disabled={!codeEnabled || codeSaving || !/^\d{4}$/.test(attendanceCode)}
                onClick={() => void handleSaveCode()}
                className="rounded-full bg-white px-4 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {codeSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {codeError ? (
            <p
              className="mt-3 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
              role="alert"
            >
              {codeError}
            </p>
          ) : null}
        </div>

        <div className="rounded-2xl border-2 border-ink/10 bg-paper/40 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="font-display text-base font-semibold text-ink">Attendance QR code</h3>
              <p className="mt-1 font-sans text-sm text-ink/65">
                Parents and guardians can scan this QR code or open the link
                directly to land on the check-in / check-out page when they drop
                off or pick up their children.
              </p>
            </div>
            <label className="block shrink-0">
              <span className="sr-only">Print QR codes</span>
              <select
                className="rounded-full border-2 border-ink/10 bg-white px-4 py-2 font-sans text-sm font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60"
                defaultValue=""
                onChange={(event) => {
                  const next = Number(event.target.value) as PrintQrCount;
                  if (next === 1 || next === 2 || next === 4 || next === 6) {
                    setPrintCount(next);
                  }
                  event.target.value = '';
                }}
              >
                <option value="" disabled>
                  Print…
                </option>
                {PRINT_OPTIONS.map((option) => (
                  <option key={option.count} value={option.count}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-4 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <div className="rounded-2xl border-2 border-ink/10 bg-white p-4">
              <QRCodeSVG value={attendanceUrl} size={160} level="M" includeMargin={false} />
            </div>
            <div className="min-w-0">
              <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                URL
              </p>
              <a
                href={attendanceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 block break-all font-sans text-sm font-semibold text-ink underline decoration-ink/30 underline-offset-2 transition hover:text-ink/80 hover:decoration-ink/60"
              >
                {attendanceUrl}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className={activeTab === 'attendance' ? '' : 'hidden'}>
        <AttendanceHistoryPanel school={school} />
      </div>
    </div>
  );
};

export default CheckInSettingsTab;
